'use client'

import type { ReactNode } from 'react'
import Image from 'next/image'

import { SidebarInset, SidebarProvider } from '@/components/ui/sidebar'
import { AppSidebar } from '@/components/app-sidebar'
import { BlogWordmark } from '@/components/blog-wordmark'
import { FooterContainer } from '@/components/footer-container'
import { Navbar } from '@/components/navbar'
import { blogUrl, siteAuth, siteLogo, siteMenu } from '@/components/navigation/menu-config'
import { Link, usePathname } from '@/i18n/navigation'

interface AppLayoutProps {
    children: ReactNode
}

const NAVBAR_HIDDEN_ROUTES = ['/deal-me-a-trip']

export function AppLayout({ children }: AppLayoutProps) {
    const pathname = usePathname()
    const hideNavbar = NAVBAR_HIDDEN_ROUTES.some((route) => pathname.includes(route))
    // Blog routes (/blog and everything under it) carry "The Window Seat"
    // masthead wordmark beside the logo in the otherwise-standard navbar.
    const isBlog = pathname.includes('/blog')

    return (
        <div className="flex min-h-screen w-full flex-col">
            {hideNavbar ? (
                <>
                    <header className="w-full border-b bg-background">
                        <div className="container mx-auto flex h-16 items-center px-4 md:px-6">
                            <Link href="/">
                                <Image
                                    src={siteLogo.src}
                                    width={siteLogo.width}
                                    height={siteLogo.height}
                                    alt={siteLogo.alt}
                                    className="h-8 w-auto"
                                />
                            </Link>
                        </div>
                    </header>
                    <main className="flex w-full flex-1 flex-col">{children}</main>
                </>
            ) : (
                <SidebarProvider defaultOpen={false} className="!min-h-0 flex-1">
                    <AppSidebar menu={siteMenu} auth={siteAuth} />
                    <SidebarInset>
                        <Navbar
                            logo={siteLogo}
                            menu={siteMenu}
                            auth={siteAuth}
                            brandSlot={isBlog ? <BlogWordmark /> : undefined}
                            brandUrl={isBlog ? blogUrl : undefined}
                        />
                        <main className="flex w-full flex-col">{children}</main>
                    </SidebarInset>
                </SidebarProvider>
            )}
            <FooterContainer />
        </div>
    )
}
